import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useUIPreference } from '@/hooks/useUIPreference';

type ThemeValue = 'light' | 'dark' | 'system';
const VALID: ReadonlyArray<ThemeValue> = ['light', 'dark', 'system'];

/**
 * Syncs the server-side `preferences.ui.theme` value into `next-themes` so
 * theme selection propagates across devices. next-themes remains the DOM
 * renderer (and keeps its own localStorage as a cold-load fast-path) — this
 * component only reconciles the stored preference onto next-themes when the
 * two diverge (e.g. after `/me` hydrates, a focus-refetch updates prefs, or a
 * BroadcastChannel message from another tab arrives).
 *
 * Writing back to the preference store happens in the header toggle handler,
 * not here, so there's a single source of outbound writes and no feedback
 * loop (the `!==` guard below short-circuits once values align).
 */
export function ThemeSync() {
  const { theme, setTheme } = useTheme();
  const [storedTheme] = useUIPreference<ThemeValue>('theme', 'system');

  useEffect(() => {
    if (!VALID.includes(storedTheme)) return;
    if (storedTheme !== theme) {
      setTheme(storedTheme);
    }
  }, [storedTheme, theme, setTheme]);

  return null;
}
